import { useState, useEffect, useCallback, useContext } from "react"
import { ConnectIoTContext } from "../../../websocket/connect-iot"
import { AnnotationMessage, MessageType, Point } from "../types/annotation-types"
import { MeetingInfoContext } from "../../../context"
import { getColorForUser } from "../utils/color-utils"
import { UserJoinInformation } from "../../../types"

interface UseAnnotationMessagesParams {
  joinInfo: UserJoinInformation
  onMessage: (message: AnnotationMessage) => void
}

export const useAnnotationMessages = ({
  joinInfo,
  onMessage,
}: UseAnnotationMessagesParams) => {

  const {
    userId,
    connected,
    channelName,
    canAnnotate,
    setCanAnnotate,
  } = useContext(MeetingInfoContext)

  const startColor = getColorForUser(userId, joinInfo.joinInfo, joinInfo.joinNumber)

  const { subscribeToChannel, sendMessage, iotConnected } = useContext(ConnectIoTContext)
  const [mouseBuffer, setMouseBuffer] = useState<Point | null>(null)
  const [lastMouseSend, setLastMouseSend] = useState<number>(0)

  const sendAnnotationMessage = useCallback((message: AnnotationMessage) => {
    const payload = JSON.stringify(message)
    sendMessage(channelName, payload)
  }, [
    userId,
    channelName,
  ])

  const shouldProcessMessage = (
    message: AnnotationMessage,
    userId: string,
    channelName: string,
    topicName: string
  ): boolean => {
    if (topicName !== channelName) {
      console.warn("~~ivai - Received message from unexpected channel", topicName)
      return false
    }

    if (message.sender === userId) {
      return false
    }

    if (message.target && message.target !== userId) {
      return false
    }

    return true
  }

  const respondToMessage = useCallback((topicName: string, payload: string) => {
    try {
      const message = JSON.parse(payload) as AnnotationMessage

      if (!shouldProcessMessage(message, userId, channelName, topicName)) {
        return
      }

      onMessage(message)

    } catch (e: any) {
      console.error("~~ivai - Error parsing message", e)
    }
  }, [
    userId,
    channelName,
  ])

  // Subscribe to channel and request initial points
  useEffect(() => {
    const waitForConnection = async () => {
      const response = await subscribeToChannel(channelName, respondToMessage)

      if (response.statusCode !== 200) {
        console.error("~~ivai - Error subscribing to annotation channel", response)
        return
      }

      setCanAnnotate(true)
    }

    if (!connected || !iotConnected || canAnnotate) {
      return
    }

    console.log("~~ivai - waiting for connection")
    waitForConnection()

  }, [connected, iotConnected, canAnnotate, channelName, respondToMessage, setCanAnnotate, userId, startColor, subscribeToChannel, sendAnnotationMessage])

  useEffect(() => {
    if (!connected || !iotConnected || !canAnnotate) {
      return
    }

    // Request points from other users
    const message: AnnotationMessage = {
      sender: userId,
      type: MessageType.RequestPoints,
      color: startColor,
    }

    sendAnnotationMessage(message)

    console.log("~~ivai - connected requesting state from other users")
  }, [connected, iotConnected, canAnnotate, sendAnnotationMessage])

  // Handle mouse updates with throttling
  useEffect(() => {
    if (!connected || !mouseBuffer || Date.now() - lastMouseSend < 50) {
      return
    }

    const message: AnnotationMessage = {
      sender: userId,
      type: MessageType.MouseUpdate,
      x: mouseBuffer.x,
      y: mouseBuffer.y,
    }

    sendAnnotationMessage(message)
    setMouseBuffer(null)
    setLastMouseSend(Date.now())
  }, [mouseBuffer, connected, userId, sendAnnotationMessage, channelName, lastMouseSend])

  const updateMouseBuffer = useCallback((point: Point) => {
    setMouseBuffer(point)
  }, [])

  return {
    updateMouseBuffer,
    sendAnnotationMessage,
  }
}
